import { Check, Copy, Edit, Eye, Trash2 } from 'lucide-react';
import Button from '../../../components/Button';
import StatusBadge from './StatusBadge';
import type { LearningLesson } from '../services/learningTypes';

interface LessonCardProps {
    lesson: LearningLesson;
    isCopied: boolean;
    onCopy: () => void;
    onEdit: () => void;
    onDelete: () => void;
    onPreview: () => void;
}

const LessonCard = ({ lesson, isCopied, onCopy, onEdit, onDelete, onPreview }: LessonCardProps) => (
    <div className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors duration-200">
        <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
            {lesson.imageSrc ? (
                <img
                    src={lesson.imageSrc}
                    alt={lesson.title}
                    className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                />
            ) : (
                <div className="w-16 h-16 rounded-lg bg-gray-200 flex-shrink-0" />
            )}
            <div className="flex-1 min-w-0">
                <h4 className="font-medium text-gray-900 break-words">{lesson.title}</h4>
                <p className="text-sm text-gray-600 mt-1 break-words">{lesson.description}</p>
                <div className="flex flex-wrap items-center gap-3 mt-2">
                    <StatusBadge status={lesson.status} />
                    <span className="text-xs text-gray-500">
                        {lesson.timeMinutes} min
                    </span>
                    <span className="text-xs text-gray-500">
                        {lesson.type}
                    </span>
                    <span className="text-xs text-gray-500">
                        Order: {lesson.order || 0}
                    </span>
                    {lesson.isFree ? (
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Free</span>
                    ) : (
                        <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">Premium</span>
                    )}
                </div>
            </div>
            <div className="flex items-center gap-2">
                <Button
                    onClick={onPreview}
                    variant="outline"
                    size="sm"
                    icon={<Eye size={16} />}
                    title="Preview as user"
                />
                <Button
                    onClick={onCopy}
                    variant="outline"
                    size="sm"
                    icon={isCopied ? <Check size={16} className="text-green-600" /> : <Copy size={16} />}
                    title="Copy lesson to clipboard"
                />
                <Button
                    onClick={onEdit}
                    variant="primary"
                    size="sm"
                    icon={<Edit size={16} />}
                />
                <Button
                    onClick={onDelete}
                    variant="danger"
                    size="sm"
                    icon={<Trash2 size={16} />}
                />
            </div>
        </div>
    </div>
);

export default LessonCard;
